// Bot faceoff input — target pick + Pass commit.
//
// Owns:   how a bot-controlled centre fills its input during the
//         Faceoff phase so that the sim-side intent collector in
//         `packages/sim/src/rules/faceoff.ts` (a) recognises the
//         chosen target via stick direction and (b) sees a single
//         Pass-button press edge at the bot's chosen commit tick.
// Reads:  state.score, state.clock.periodIndex/remainingTicks/tick,
//         player.teamId, tuning.clock.
// Mutates: input.moveX / input.moveY / input.buttons / input.sprint.
//
// Strategy (deterministic, RNG-seeded per faceoff for variety):
//   - Pick a target (LW/RW/LD/RD) based on score + period.
//     Trailing late → favour a forward (LW or RW) to push for offence.
//     Leading late  → favour a defenceman (LD or RD) to retreat with
//                     the puck and burn clock.
//     Otherwise → favour a forward, slight randomness on which.
//   - Pick a commit tick somewhere in the latter ~half of the window
//     so the bot accumulates a decent stability bonus before pressing
//     Pass. Late-but-not-last gives reasonable timing without always
//     pushing to the literal final tick.
//
// `pickFaceoffTarget` returns the target enum.
// `pickFaceoffCommitTick` returns the relative tick into the window
//   where Pass should fire.
// `fillCenterFaceoffInput` writes the matching moveX / moveY and sets
//   the Pass button on the commit tick only (so the sim's edge
//   detector latches once).
//
// All RNG calls go through a per-faceoff seed derived from
// (teamId, score, periodIndex) so a given match plays out the same on
// rerun.

import { InputButton, type PlayerInput } from "@arcade-hockey/shared";
import type { MatchState, PlayerState } from "@arcade-hockey/sim";
import { tuning } from "@arcade-hockey/sim";

export type FaceoffTargetPick = "LW" | "RW" | "LD" | "RD";

// Small local LCG seeded off the team + score so the same game state
// produces the same bot pick. Decoupled from `state.rng` so the bot's
// picks don't disturb the sim RNG sequence.
function deterministicHash(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function pickFromSeed(seed: number, n: number): number {
  // Mulberry32-style mix.
  let x = seed | 0;
  x = (x + 0x6d2b79f5) | 0;
  x = Math.imul(x ^ (x >>> 15), x | 1);
  x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
  const r = ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  return Math.floor(r * n);
}

/**
 * Pick this team's faceoff target. Deterministic given the same
 * (teamId, score, periodIndex) tuple.
 */
export function pickFaceoffTarget(
  state: MatchState,
  teamId: PlayerState["teamId"],
): FaceoffTargetPick {
  const ourScore = teamId === "home" ? state.score.home : state.score.away;
  const theirScore = teamId === "home" ? state.score.away : state.score.home;
  const diff = ourScore - theirScore;
  const lateInPeriod = state.clock.periodIndex === 2 && state.clock.remainingTicks < 60 * 60 * 2;

  const seed = deterministicHash(
    `${teamId}|${state.score.home}|${state.score.away}|${state.clock.periodIndex}`,
  );

  if (lateInPeriod && diff <= -2) {
    // Trailing big — pick a winger (forward) for an attacking rush.
    return pickFromSeed(seed, 2) === 0 ? "LW" : "RW";
  }
  if (lateInPeriod && diff >= 2) {
    // Leading big — pick a D for clock-killing breakout.
    return pickFromSeed(seed, 2) === 0 ? "LD" : "RD";
  }
  // Default: forward. Slight randomness on which winger.
  return pickFromSeed(seed, 2) === 0 ? "LW" : "RW";
}

/**
 * Pick the tick (relative to the start of the Faceoff window) at
 * which the bot should press Pass. Lands in the back half of the
 * window with a deterministic spread so different matches produce
 * different commit timings.
 */
export function pickFaceoffCommitTick(state: MatchState, teamId: PlayerState["teamId"]): number {
  const seed = deterministicHash(
    `${teamId}|${state.score.home}|${state.score.away}|commit|${state.clock.periodIndex}`,
  );
  // Range: close to the drop. Stability builds over the first ~80%
  // of the window; bot fires Pass in the last ~25% so the timing
  // score is meaningful. Cap so the bot never literally fires AT the
  // drop tick — leave a few ticks of slack for natural variation.
  const totalTicks = tuning.clock.faceoffPauseTicks; // 180
  const minRel = Math.floor(totalTicks * 0.75); // ~135
  const spread = Math.floor(totalTicks * 0.22); // ~39 → commit in [135..174]
  const offset = pickFromSeed(seed, spread);
  return minRel + offset;
}

// Stick push magnitudes used to direct the target. Use the strong
// push (1.0) so the bot's stick is unambiguously in the quadrant.
const PUSH = 1.0;

/**
 * Fill a centre's faceoff input. Writes (moveX, moveY) to encode the
 * chosen target's quadrant and sets the Pass button only on the
 * bot's chosen commit tick.
 */
export function fillCenterFaceoffInput(
  input: PlayerInput,
  player: PlayerState,
  state: MatchState,
): void {
  const target = pickFaceoffTarget(state, player.teamId);
  const attackDir = player.teamId === "home" ? 1 : -1;

  // moveX encodes forward/back, moveY encodes left/right.
  // forward+left → LW, forward+right → RW, back+left → LD, back+right → RD.
  const isForward = target === "LW" || target === "RW";
  const isLeft = target === "LW" || target === "LD";
  input.moveX = (isForward ? attackDir : -attackDir) * PUSH;
  input.moveY = isLeft ? PUSH : -PUSH;
  input.sprint = 0;

  // Edge-fire Pass on the chosen commit tick. The sim's
  // `collectFaceoffIntents` does its own rising-edge detection, so a
  // single tick with Pass held is enough.
  const ticksIntoWindow = tuning.clock.faceoffPauseTicks - state.clock.remainingTicks;
  const targetCommit = pickFaceoffCommitTick(state, player.teamId);
  input.buttons = ticksIntoWindow === targetCommit ? InputButton.Pass : 0;
}
